import { LoadingButton } from "@mui/lab";
import { Button, Chip, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { PromoModel } from "../../../../domain/features/promotions/models/PromoModel";
import { isNullish } from "../../../../utils/Functions";
import { promoActions } from "../../../shared/constants/Actions";
import { useAuth } from "../../../shared/hooks/authentication/useAuth";
import { Pages } from "../../../shared/pages";
import { StyledTableCell, StyledTableRow } from "./PromotionsTable.styles";

interface PromotionTableRowProps {
  readonly promo: PromoModel;
  refetchPromos: () => Promise<unknown>;
}

export const PromotionTableRow: React.FC<PromotionTableRowProps> = ({ promo, refetchPromos }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("promotionsTable");
  const { organization } = useAuth();
  const organizationId = organization?.id;

  const isActivePromo = promo.active;
  const isEditablePromo = isNullish(promo.code) && !promo.active;

  const {
    isLoading: isDeactivating,
    mutate: deactivatePromo,
  } = useMutation([ "deactivatePromotion", promo.id ], async () => {
    await promoActions.deactivate(organizationId!, promo.id);
    return refetchPromos();
  });

  const {
    mutate: activatePromo,
    isLoading: isActivatingPromo,
  } = useMutation([ "activatePromotion", promo.id ], async () => {
    await promoActions.activate(organizationId!, promo.id);
    return refetchPromos();
  });

  const onDeactivateClick = () => {
    return deactivatePromo();
  };

  return (
    <StyledTableRow>
      <StyledTableCell>
        <Chip
          label={t(isActivePromo ? "activated" : isEditablePromo ? "draft" : "deactivated")}
          color={isActivePromo ? "success" : isEditablePromo ? "warning" : "default"}
          sx={{ minWidth: "90px" }}
        />
      </StyledTableCell>
      <StyledTableCell>
        <Typography>{promo.code ?? "N/A"}</Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography>{promo.name}</Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="center"
          sx={{ "&.MuiButton-root": { minWidth: "121px" } }}
        >
          <Button
            onClick={() => navigate(Pages.promoEdit(promo.id))}
            variant="outlined"
          >
            {t(isEditablePromo ? "edit" : "details")}
          </Button>
          {isEditablePromo &&
            <LoadingButton variant="outlined" loading={isActivatingPromo} onClick={() => activatePromo()}>
              {t("activate")}
            </LoadingButton>
          }
          {isActivePromo
            ? <LoadingButton variant="outlined" loading={isDeactivating} onClick={onDeactivateClick}>
              {t("deactivate")}
            </LoadingButton>
            : null}
          {/*Todo: handle activate promo here */}
        </Stack>
       
      </StyledTableCell>
    </StyledTableRow>
  );
};

